import React, { useEffect, useState, useCallback, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import {
  Plus, Send, Loader2, X, Trash2, CheckCircle2, FileText, Download,
  ArrowLeft, Building2, Mail, Calendar, Hash, ShieldCheck, Sparkles,
} from "lucide-react";

// ── Design tokens — deliberately NOT the default Tailwind blue/slate combo. ─
// A deep ink-navy chrome with a warm brass/gold accent reads as financial /
// enterprise (think a private aviation invoice, not a to-do app). Used as
// inline styles in the few spots Tailwind's default palette can't reach,
// and as utility classes everywhere else.
const NAVY = "#0B1220";       // near-black ink navy — headers, primary CTAs
const NAVY_SOFT = "#141F38";  // secondary panels on dark
const GOLD = "#B8862E";       // brass/gold — money, totals, premium accents
const GOLD_LIGHT = "#D9A94A";
const BORDER_BRASS = "rgba(184,134,46,0.3)";

// Enterprise-grade rebuild. Previously this page was 100% hardcoded — one
// fixed mock invoice, a "Send" button with no onClick, a "Download PDF"
// button that did nothing. Now a real two-step flow wired to the backend:
//   GET  /api/loads/assignments/mine/   -> which completed jobs need invoicing
//   GET  /api/invoices/                 -> real invoices, with driver/job
//                                          info the backend derives for you
//                                          (name, email, phone, PPCA #,
//                                          route, load description) so you
//                                          don't have to type it twice
//   POST /api/invoices/generate/        -> {assignment_id, line_items,
//                                            due_date, bill_to_company,
//                                            bill_to_email, permit_number}
//   POST /api/invoices/<id>/send/       -> actually emails it (Celery)
//
// This round: (1) the builder modal no longer overlaps itself on narrow
// screens — it becomes a full-height bottom sheet under the sm breakpoint
// instead of a fixed-width card squeezed into 387px; (2) a premium ink-navy
// / brass visual identity replaces the generic blue/slate one; (3) the
// builder auto-opens the moment a completed job has no invoice yet, so
// drafting starts immediately instead of the driver having to go find the
// button; (4) the send screen's email field now defaults to the billing
// email actually entered in the builder, not the driver's own account email.
const AUTO_PROMPT_KEY = "pilotlink_invoice_auto_prompted_v1";

export default function InvoicePage() {
  const { user } = useOutletContext() || {};
  const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
  const token = localStorage.getItem("token") || localStorage.getItem("user_token") || localStorage.getItem("access_token");

  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [uninvoicedJobs, setUninvoicedJobs] = useState([]);
  const [error, setError] = useState(null);
  const [builderJob, setBuilderJob] = useState(null);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [sendingId, setSendingId] = useState(null);

  const refresh = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const [invRes, jobsRes] = await Promise.all([
        fetch(`${apiUrl}/api/invoices/`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiUrl}/api/loads/assignments/mine/`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const invList = invRes.ok ? await invRes.json() : [];
      const jobsList = jobsRes.ok ? await jobsRes.json() : [];
      setInvoices(invList);
      const invoicedAssignmentIds = new Set(invList.map(i => i.assignment));
      setUninvoicedJobs((jobsList || []).filter(a => a.status === "completed" && !invoicedAssignmentIds.has(a.id)));
      setError(null);
    } catch (err) {
      setError("Couldn't load invoices. " + err.message);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token]);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Auto-open the builder the moment a completed job has no invoice yet.
  // Ready-to-draft shouldn't wait for the driver to notice a list item and
  // tap "Create Invoice" — the job just finished, so the draft opens on its
  // own. Each job only triggers this once (tracked in localStorage) so
  // closing the modal doesn't reopen it on every page visit.
  useEffect(() => {
    if (builderJob || previewInvoice || uninvoicedJobs.length === 0) return;
    let seen = [];
    try { seen = JSON.parse(localStorage.getItem(AUTO_PROMPT_KEY) || "[]"); } catch { /* ignore */ }
    const nextJob = uninvoicedJobs.find(j => !seen.includes(j.id));
    if (nextJob) {
      setBuilderJob(nextJob);
      try { localStorage.setItem(AUTO_PROMPT_KEY, JSON.stringify([...seen, nextJob.id].slice(-200))); } catch { /* ignore */ }
    }
  }, [uninvoicedJobs, builderJob, previewInvoice]);

  const handleSend = async (invoiceId, email) => {
    setSendingId(invoiceId);
    try {
      const res = await fetch(`${apiUrl}/api/invoices/${invoiceId}/send/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(email ? { email } : {}),
      });
      if (res.ok) {
        const updated = await res.json();
        setInvoices(prev => prev.map(i => i.id === invoiceId ? updated : i));
        setPreviewInvoice(prev => prev?.id === invoiceId ? updated : prev);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || "Couldn't send invoice. The email server may be unreachable — try again in a moment.");
      }
    } catch (err) {
      alert("Network error sending invoice.");
    } finally {
      setSendingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading invoices…
      </div>
    );
  }

  if (previewInvoice) {
    return (
      <InvoicePreview
        invoice={previewInvoice}
        user={user}
        onBack={() => setPreviewInvoice(null)}
        onSend={(email) => handleSend(previewInvoice.id, email)}
        sending={sendingId === previewInvoice.id}
      />
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.15em]" style={{ color: GOLD }}>PilotLinkUSA</p>
        <h2 className="text-xl font-black tracking-tight" style={{ color: NAVY }}>Invoices</h2>
        <p className="text-xs text-gray-400 mt-0.5">Generate and send invoices for completed jobs</p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {uninvoicedJobs.length > 0 && (
        <div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Ready to invoice</p>
          <div className="space-y-2">
            {uninvoicedJobs.map(job => (
              <div key={job.id} className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center justify-between gap-3 shadow-sm">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: GOLD }} />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: GOLD }} />
                    </span>
                    <p className="text-sm font-bold text-gray-900 truncate">{job.load?.from} → {job.load?.to}</p>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5 pl-3.5">Completed · Draft ready · {job.load?.id || job.id.slice(0, 8)}</p>
                </div>
                <button
                  onClick={() => setBuilderJob(job)}
                  className="flex items-center gap-1.5 text-xs font-bold text-white px-3.5 py-2.5 rounded-xl flex-shrink-0 shadow-sm hover:opacity-90 transition-opacity"
                  style={{ background: NAVY }}
                >
                  <Plus className="w-3.5 h-3.5" /> Create Invoice
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Your invoices</p>
        {invoices.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center">
            <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No invoices yet — complete a job to create one.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
            <div className="hidden sm:grid grid-cols-12 gap-4 px-5 py-2.5 bg-gray-50 border-b border-gray-100 text-[10px] font-black uppercase tracking-[0.1em] text-gray-400">
              <div className="col-span-3">Reference</div>
              <div className="col-span-5">Client / Route</div>
              <div className="col-span-2 text-right">Amount</div>
              <div className="col-span-2 text-right">Status</div>
            </div>
            {invoices.map((inv, i) => (
              <button
                key={inv.id}
                onClick={() => setPreviewInvoice(inv)}
                className="w-full text-left grid grid-cols-2 sm:grid-cols-12 gap-2 sm:gap-4 px-5 py-4 items-center hover:bg-gray-50 transition-colors"
                style={{ borderBottom: i === invoices.length - 1 ? "none" : "1px solid #f3f4f6" }}
              >
                <div className="col-span-2 sm:col-span-3 flex items-center gap-3 min-w-0">
                  <div className="p-1.5 rounded flex-shrink-0 bg-gray-50 text-gray-400">
                    <FileText className="w-3.5 h-3.5" />
                  </div>
                  <p className="text-sm font-bold truncate" style={{ color: NAVY }}>#{inv.invoice_number}</p>
                </div>
                <div className="col-span-2 sm:col-span-5 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{inv.bill_to_company || "No company on file"}</p>
                  <p className="text-[11px] text-gray-400 truncate">{inv.load_route}</p>
                </div>
                <div className="text-sm font-black sm:text-right sm:col-span-2" style={{ color: GOLD }}>${Number(inv.total).toLocaleString()}</div>
                <div className="sm:text-right sm:col-span-2">
                  <span
                    className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full inline-block"
                    style={
                      inv.status === "sent" ? { background: "rgba(16,185,129,0.1)", color: "#10B981" } :
                      inv.status === "paid" ? { background: "rgba(37,99,235,0.1)", color: "#2563EB" } :
                      { background: "rgba(184,134,46,0.04)", border: "1px solid rgba(184,134,46,0.3)", color: "#8A651F" }
                    }
                  >{inv.status}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {builderJob && (
        <InvoiceBuilderModal
          job={builderJob}
          user={user}
          apiUrl={apiUrl}
          token={token}
          onClose={() => setBuilderJob(null)}
          onCreated={(invoice) => {
            setBuilderJob(null);
            setInvoices(prev => [invoice, ...prev]);
            setUninvoicedJobs(prev => prev.filter(j => j.id !== builderJob.id));
            setPreviewInvoice(invoice);
          }}
        />
      )}
    </div>
  );
}

// ── Step 1: Builder — QuickBooks-style form ─────────────────────────────────
// Driver identity fields (name/email/phone/PPCA#) are shown read-only,
// pulled from the real account — no point re-typing what's already on
// file. Bill-to defaults to the dispatcher's company (pre-filled by the
// backend) but stays editable, since the actual billing contact can differ.
// Builds the same kind of smart default the backend falls back to (see
// loads/invoice_views.py::_default_line_items), but instantly client-side
// using data the load-board API already sends (pay_type/mileage_rate/
// hourly_rate/min_daily_pay/height_pole_pay/overnight/escort_addons) —
// this is the "how it calculates from the job offer" the invoice reflects,
// not a guess. Distance/hours actually driven aren't tracked precisely
// anywhere in the schema, so mileage/hourly rates show as a rate
// reference for the driver to fill the real total into, rather than a
// fabricated computed number.
function buildDefaultItems(load) {
  const money = (v) => { const n = parseFloat(String(v || "").replace(/[$,]/g, "")); return Number.isFinite(n) ? n : null; };
  const items = [];

  const mileage = money(load?.mileage_rate);
  const hourly = money(load?.hourly_rate);
  const minDaily = money(load?.min_daily_pay);
  const payType = String(load?.pay_type || "").toLowerCase();
  const looksFlat = payType.includes("flat") || payType.includes("fixed") || payType.includes("escort");

  if (looksFlat && load?.pay) {
    items.push({ label: `Escort Service (rate: ${load.pay} — enter agreed amount)`, amount: "" });
  }
  if (mileage != null) {
    items.push({ label: `Mileage Rate ($${load.mileage_rate}/mi)`, amount: "", rate: mileage, unit: "mi", qty: "" });
  }
  if (hourly != null) {
    items.push({ label: `Hourly Rate ($${load.hourly_rate}/hr)`, amount: "", rate: hourly, unit: "hr", qty: "" });
  }
  if (minDaily != null) {
    items.push({ label: "Daily Minimum", amount: minDaily });
  }
  if (items.length === 0) {
    items.push({ label: "Escort Service", amount: "" });
  }

  const fuel = money(load?.fuel_surcharge); // "TBD" placeholder text -> null correctly; real numbers pass through
  if (fuel != null) {
    items.push({ label: "Fuel Surcharge", amount: fuel });
  }
  if (load?.needs_height_pole && money(load?.height_pole_pay) != null) {
    items.push({ label: "Height Pole", amount: money(load.height_pole_pay) });
  }
  if (load?.overnight) {
    items.push({ label: "Overnight Stay", amount: money(load?.overnight_rate) ?? 125 });
  }
  if (money(load?.escort_addons) != null) {
    items.push({ label: "Escort Add-ons", amount: money(load.escort_addons) });
  }
  return items;
}

function InvoiceBuilderModal({ job, user, apiUrl, token, onClose, onCreated }) {
  const driverProfile = user?.driverProfile || user?.driver_profile || {};
  const [items, setItems] = useState(() => buildDefaultItems(job.load));
  const [extraCharges, setExtraCharges] = useState([]);
  const [dueDate, setDueDate] = useState("");
  const [billToCompany, setBillToCompany] = useState(job.load?.company || job.dispatcher?.full_name || "");
  const [billToEmail, setBillToEmail] = useState(job.dispatcher?.email || "");
  const [permitNumber, setPermitNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  const allItems = [...items, ...extraCharges];
  const total = allItems.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);

  const updateItem = (idx, field, value) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  const updateQty = (idx, qty) => setItems(prev => prev.map((it, i) => {
    if (i !== idx) return it;
    const q = parseFloat(qty);
    const computed = it.rate != null && Number.isFinite(q) ? +(it.rate * q).toFixed(2) : it.amount;
    return { ...it, qty, amount: computed };
  }));
  const addItem = () => setItems(prev => [...prev, { label: "", amount: "" }]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const updateExtra = (idx, field, value) => setExtraCharges(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  const addExtra = () => setExtraCharges(prev => [...prev, { reason: "", amount: "" }]);
  const removeExtra = (idx) => setExtraCharges(prev => prev.filter((_, i) => i !== idx));

  const generate = async () => {
    setSaving(true); setError(null);
    try {
      // Carry qty/rate/unit through to the backend, not just label+amount —
      // otherwise the invoice preview and the emailed PDF have no way to
      // show "620 mi @ $2.50" and can only ever render "—" for Qty/Rate,
      // even though the driver entered real numbers in the builder.
      const combined = [
        ...items.filter(i => i.label.trim()).map(i => ({
          label: i.label,
          amount: parseFloat(i.amount) || 0,
          ...(i.qty ? { qty: i.qty, unit: i.unit || null, rate: i.rate ?? null } : {}),
        })),
        ...extraCharges.filter(e => e.reason.trim()).map(e => ({ label: `Additional — ${e.reason}`, amount: parseFloat(e.amount) || 0 })),
      ];
      const res = await fetch(`${apiUrl}/api/invoices/generate/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          assignment_id: job.id,
          line_items: combined,
          due_date: dueDate || null,
          bill_to_company: billToCompany,
          bill_to_email: billToEmail,
          permit_number: permitNumber,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Server returned ${res.status}`);
      }
      onCreated(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    // Bottom-sheet on mobile, centered dialog from `sm:` up. This is the
    // actual fix for the overlap in the screenshot: a fixed max-w-2xl card
    // with p-4 grids inside it was being squeezed into a 387px viewport
    // with no responsive fallback, so the two-column From/Bill-To grid and
    // the Due Date/Job ID grid collided. Below sm, everything stacks to a
    // single column and the sheet takes the full width/height instead.
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-xl rounded-t-3xl sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col max-h-[94vh] sm:max-h-[88vh] sm:my-8"
        onClick={e => e.stopPropagation()}
      >
        {/* Header — ink navy with a hairline brass rule, drag handle on mobile */}
        <div className="flex-shrink-0" style={{ background: NAVY }}>
          <div className="sm:hidden flex justify-center pt-2.5 pb-1">
            <div className="w-9 h-1 rounded-full bg-white/20" />
          </div>
          <div className="px-5 sm:px-6 pt-3 pb-4 sm:pt-5 sm:pb-5 flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border" style={{ borderColor: "rgba(184,134,46,0.4)", background: NAVY_SOFT }}>
                <ShieldCheck className="w-4.5 h-4.5" style={{ color: GOLD_LIGHT }} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-black uppercase tracking-[0.15em]" style={{ color: GOLD_LIGHT }}>PilotLinkUSA</p>
                <h3 className="font-black text-white text-base sm:text-lg leading-tight truncate">Invoice Builder</h3>
              </div>
            </div>
            <button onClick={onClose} className="text-white/50 hover:text-white p-1.5 -mr-1.5 -mt-1 flex-shrink-0" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
        </div>

        {/* Body — scrolls independently so the header and footer (total +
            submit) stay pinned in view; the submit action is never below
            the fold on mobile the way it was before. */}
        <div ref={scrollRef} className="p-5 sm:p-6 space-y-6 overflow-y-auto flex-1 min-h-0">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Due Date" icon={Calendar}>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:outline-none"
                style={{ "--tw-ring-color": GOLD }} />
            </Field>
            <Field label="Job ID" icon={Hash}>
              <input disabled value={job.load?.id || job.id.slice(0, 8)} className="w-full px-3 py-2.5 border border-gray-200 bg-gray-50 rounded-lg text-sm text-gray-500 truncate" />
            </Field>
          </div>

          {/* From — Driver / Bill To — Company: single column under sm,
              two columns from sm up. This pair was the other overlap
              source — a raw grid-cols-2 with no mobile fallback. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/60">
              <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3">From — Driver</p>
              <p className="text-sm font-bold text-gray-900 truncate">{user?.full_name}</p>
              <p className="text-xs text-gray-500 mt-1 truncate">{user?.email}</p>
              <p className="text-xs text-gray-500">{driverProfile.phone || user?.phone || "No phone on file"}</p>
              {driverProfile.ppca_cert_number && (
                <p className="text-[10px] font-bold mt-2" style={{ color: GOLD }}>PPCA #{driverProfile.ppca_cert_number}</p>
              )}
            </div>
            <div className="border border-gray-200 rounded-xl p-4">
              <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                <Building2 className="w-3 h-3" /> Bill To — Company
              </p>
              <input
                value={billToCompany}
                onChange={e => setBillToCompany(e.target.value)}
                placeholder="Company name"
                className="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-sm mb-2 focus:ring-2 focus:outline-none"
              />
              <div className="relative">
                <Mail className="w-3.5 h-3.5 text-gray-300 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  value={billToEmail}
                  onChange={e => setBillToEmail(e.target.value)}
                  placeholder="billing@company.com"
                  type="email"
                  className="w-full pl-8 pr-2.5 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:outline-none"
                />
              </div>
              <p className="text-[10px] text-gray-400 mt-1.5">This is the address invoices will send to by default.</p>
            </div>
          </div>

          {/* Job details */}
          <div>
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Job Details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm bg-gray-50 rounded-xl p-3 border border-gray-100">
              <div><p className="text-[10px] text-gray-400 font-bold uppercase">Route</p><p className="font-semibold text-gray-800">{job.load?.from} → {job.load?.to}</p></div>
              <div><p className="text-[10px] text-gray-400 font-bold uppercase">Load</p><p className="font-semibold text-gray-800">{job.load?.commodity || "Oversize Load"}</p></div>
            </div>
            <input
              value={permitNumber}
              onChange={e => setPermitNumber(e.target.value)}
              placeholder="State permit number (optional)"
              className="w-full mt-2 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:outline-none"
            />
          </div>

          {/* Line items — stack label above the qty/amount row on mobile so
              nothing gets squeezed into slivers. */}
          <div>
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Billing — Escort Service</p>
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={idx} className="flex flex-col sm:flex-row gap-2 p-2.5 sm:p-0 rounded-lg sm:rounded-none bg-gray-50/70 sm:bg-transparent">
                  <input value={item.label} onChange={e => updateItem(idx, "label", e.target.value)} placeholder="e.g. Escort Service, Fuel Surcharge, Overnight Stay"
                    className="flex-1 min-w-0 px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:outline-none bg-white" />
                  <div className="flex items-center gap-2">
                    {item.unit && (
                      <input
                        value={item.qty ?? ""}
                        onChange={e => updateQty(idx, e.target.value)}
                        placeholder={item.unit === "mi" ? "miles" : "hours"}
                        type="number"
                        title={`Total ${item.unit === "mi" ? "miles" : "hours"} — auto-fills amount at $${item.rate}/${item.unit}`}
                        className="w-20 flex-shrink-0 px-2 py-2.5 sm:py-2 border rounded-lg text-sm text-center focus:ring-2 focus:outline-none bg-amber-50/50"
                        style={{ borderColor: "rgba(184,134,46,0.35)" }}
                      />
                    )}
                    <input value={item.amount} onChange={e => updateItem(idx, "amount", e.target.value)} placeholder="$0" type="number"
                      className="w-24 flex-shrink-0 px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:outline-none bg-white" />
                    {items.length > 1 && (
                      <button onClick={() => removeItem(idx)} className="text-gray-300 hover:text-red-500 flex-shrink-0 p-1" aria-label="Remove line item">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={addItem} className="text-xs font-bold flex items-center gap-1 mt-2.5" style={{ color: GOLD }}>
              <Plus className="w-3.5 h-3.5" /> Add line item
            </button>
          </div>

          {/* Additional/exceptional charges */}
          <div className="border rounded-xl p-3.5" style={{ borderColor: "rgba(184,134,46,0.3)", background: "rgba(184,134,46,0.04)" }}>
            <p className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: "#8A651F" }}>
              Additional Charges <span className="font-medium normal-case text-gray-500">— route change, delay, accident, etc.</span>
            </p>
            {extraCharges.length === 0 && (
              <p className="text-xs text-gray-500 mb-2">None added — use this for anything outside the standard billing above.</p>
            )}
            <div className="space-y-2">
              {extraCharges.map((extra, idx) => (
                <div key={idx} className="flex flex-col sm:flex-row gap-2">
                  <input value={extra.reason} onChange={e => updateExtra(idx, "reason", e.target.value)} placeholder="Reason (e.g. Route changed due to accident on I-40)"
                    className="flex-1 min-w-0 px-3 py-2.5 sm:py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:outline-none" style={{ borderColor: "rgba(184,134,46,0.35)" }} />
                  <div className="flex items-center gap-2">
                    <input value={extra.amount} onChange={e => updateExtra(idx, "amount", e.target.value)} placeholder="$0" type="number"
                      className="w-24 flex-shrink-0 px-3 py-2.5 sm:py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:outline-none" style={{ borderColor: "rgba(184,134,46,0.35)" }} />
                    <button onClick={() => removeExtra(idx)} className="text-amber-300 hover:text-red-500 flex-shrink-0 p-1" aria-label="Remove charge"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={addExtra} className="text-xs font-semibold flex items-center gap-1 mt-2" style={{ color: "#8A651F" }}>
              <Plus className="w-3.5 h-3.5" /> Add additional charge
            </button>
          </div>

          {error && <p className="text-xs text-red-600 flex items-center gap-1.5">{error}</p>}
        </div>

        {/* Footer — pinned, so Total and the submit button are always
            visible without scrolling, even on short mobile viewports. */}
        <div className="flex-shrink-0 px-5 sm:px-6 py-4 bg-white border-t border-gray-100" style={{ boxShadow: "0 -6px 16px -12px rgba(0,0,0,0.18)" }}>
          <div className="flex justify-between items-center mb-3">
            <span className="font-bold text-gray-900 text-sm">Total</span>
            <span className="text-2xl font-black" style={{ color: GOLD }}>${total.toLocaleString()}</span>
          </div>
          <button
            onClick={generate}
            disabled={saving || total <= 0}
            className="w-full text-white py-3.5 rounded-xl text-sm font-bold disabled:opacity-40 transition-opacity flex items-center justify-center gap-2 shadow-sm"
            style={{ background: NAVY }}
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : <>Preview &amp; Send <Sparkles className="w-3.5 h-3.5" style={{ color: GOLD_LIGHT }} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </p>
      {children}
    </div>
  );
}

// ── Step 2: Preview — the polished, sendable view ───────────────────────────
function InvoicePreview({ invoice, user, onBack, onSend, sending }) {
  // FIX: this used to default to the driver's own account email
  // (`user?.email`) — so the send box on a fresh draft silently offered to
  // email the invoice to the driver instead of the billing contact they
  // just typed into the builder. It now defaults to whichever email was
  // actually entered under "Bill To — Company" in the Invoice Builder,
  // falling back to the driver's email only if no billing email was set.
  const [emailOverride, setEmailOverride] = useState(invoice.sent_to_email || invoice.bill_to_email || user?.email || "");

  // FIX: the subtitle used to be driven by `service_date` alone — a sent
  // invoice with no service_date on file would still show "Draft — not
  // yet issued" right next to a green "SENT" badge, which is exactly the
  // contradiction this was reported for. It's now driven by `status`,
  // the single source of truth for where the invoice actually is.
  const [showResend, setShowResend] = useState(false);
  const statusMeta = {
    draft: { dot: "#9CA3AF", bg: "#F3F4F6", text: "#6B7280", label: "Draft", subtitle: "Draft — not yet sent" },
    sent: { dot: "#10B981", bg: "rgba(16,185,129,0.1)", text: "#059669", label: "Sent", subtitle: invoice.sent_at ? `Sent ${new Date(invoice.sent_at).toLocaleDateString()}` : "Sent" },
    paid: { dot: "#2563EB", bg: "rgba(37,99,235,0.1)", text: "#2563EB", label: "Paid", subtitle: "Paid in full" },
  }[invoice.status] || { dot: "#9CA3AF", bg: "#F3F4F6", text: "#6B7280", label: invoice.status, subtitle: "" };

  return (
    <div className="max-w-xl space-y-4">
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          body * { visibility: hidden; }
          #invoice-print-area, #invoice-print-area * { visibility: visible; }
          #invoice-print-area { position: absolute; top: 0; left: 0; width: 100%; box-shadow: none !important; border: none !important; }
          #invoice-print-hide { display: none !important; }
        }
      `}} />

      <button id="invoice-print-hide" onClick={onBack} className="text-xs font-semibold text-gray-500 hover:text-gray-700 flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to invoices
      </button>

      <div id="invoice-print-area" className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm relative">
        {/* Top brass accent line — matches the "Brass Hairline" component */}
        <div className="absolute top-0 left-0 w-full h-1" style={{ background: `linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT})` }} />

        <div className="p-6 sm:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-gray-100 pb-6 mb-6">
            <div className="min-w-0">
              <p className="text-[10px] font-black uppercase tracking-[0.15em] mb-1.5" style={{ color: GOLD }}>PilotLinkUSA</p>
              <h1 className="text-2xl font-black tracking-tight truncate" style={{ color: NAVY }}>Invoice #{invoice.invoice_number}</h1>
              <p className="text-sm mt-1" style={{ color: statusMeta.text }}>{statusMeta.subtitle}{invoice.service_date ? ` · Issued ${invoice.service_date}` : ""}</p>
            </div>
            {/* Status pill — background/text color-coded, not just a dot,
                so status reads clearly at a glance instead of needing to
                parse a small gray label. */}
            <div className="px-3 py-1.5 rounded-full flex items-center gap-2 flex-shrink-0" style={{ background: statusMeta.bg }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusMeta.dot }} />
              <span className="text-[10px] font-black uppercase tracking-wider" style={{ color: statusMeta.text }}>{statusMeta.label}</span>
            </div>
          </div>

          {/* Parties */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-8">
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] mb-2">From</p>
              <p className="text-sm font-bold text-gray-900">{invoice.driver_name}</p>
              <p className="text-xs text-gray-500 mt-0.5">Professional Driver</p>
              <div className="text-xs text-gray-500 mt-1.5 space-y-0.5">
                <p>{invoice.driver_email}</p>
                {invoice.ppca_cert_number && <p style={{ color: "#0F766E" }} className="font-semibold">PPCA #{invoice.ppca_cert_number}</p>}
              </div>
            </div>
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] mb-2">Bill To</p>
              <p className="text-sm font-bold text-gray-900">{invoice.bill_to_company || "—"}</p>
              <div className="text-xs text-gray-500 mt-1.5 space-y-0.5">
                <p>{invoice.bill_to_email || "—"}</p>
              </div>
            </div>
          </div>

          {/* Job reference — brass hairline card */}
          <div className="rounded-xl p-4 sm:p-5 mb-8 flex flex-col sm:flex-row gap-4 sm:items-center border" style={{ background: "rgba(184,134,46,0.04)", borderColor: "rgba(184,134,46,0.3)" }}>
            <div className="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-full bg-white border" style={{ borderColor: "rgba(184,134,46,0.3)" }}>
              <Hash className="w-4 h-4" style={{ color: GOLD }} />
            </div>
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] mb-0.5">Job ID</p>
                <p className="text-sm font-bold text-gray-900">{invoice.job_reference}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] mb-0.5">Route &amp; Description</p>
                <p className="text-sm font-bold text-gray-900 truncate">{invoice.load_route}</p>
                <p className="text-xs text-gray-500 mt-0.5">{invoice.load_description}</p>
              </div>
              {invoice.permit_number && (
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] mb-0.5">Permit #</p>
                  <p className="text-sm font-bold text-gray-900">{invoice.permit_number}</p>
                </div>
              )}
              {invoice.due_date && (
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] mb-0.5">Due Date</p>
                  <p className="text-sm font-bold text-gray-900">{invoice.due_date}</p>
                </div>
              )}
            </div>
          </div>

          {/* Line items — Qty/Rate shown only where the backend actually
              has them (mileage/hourly items); everything else is a plain
              Description/Amount row rather than a fabricated "1 / flat". */}
          <div className="overflow-x-auto mb-8">
            <table className="w-full text-left border-collapse min-w-[420px]">
              <thead>
                <tr>
                  <th className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] border-b border-gray-100 py-2.5 w-1/2">Description</th>
                  <th className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] border-b border-gray-100 py-2.5 text-right">Qty</th>
                  <th className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] border-b border-gray-100 py-2.5 text-right">Rate</th>
                  <th className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] border-b border-gray-100 py-2.5 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(invoice.line_items || []).map((item, i) => (
                  <tr key={i}>
                    <td className="py-3 text-sm font-semibold text-gray-800">{item.label}</td>
                    <td className="py-3 text-sm text-gray-500 text-right">{item.qty ? `${item.qty} ${item.unit || ""}` : "—"}</td>
                    <td className="py-3 text-sm text-gray-500 text-right">{item.rate ? `$${Number(item.rate).toLocaleString()}` : "—"}</td>
                    <td className="py-3 text-sm font-bold text-gray-900 text-right">${Number(item.amount).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end border-t border-gray-100 pt-5">
            <div className="w-full sm:w-64 space-y-2.5">
              <div className="flex justify-between text-sm text-gray-500">
                <span>Subtotal</span>
                <span>${Number(invoice.total).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center pt-2.5 border-t border-dashed border-gray-200">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em]">Grand Total</span>
                <span className="text-2xl font-black" style={{ color: GOLD }}>${Number(invoice.total).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {invoice.status === "draft" ? (
            // Draft: the only state where "Send" is the primary action —
            // this section never renders once an invoice has actually
            // been sent, so there's no way to see a live send box next
            // to a "Sent" badge at the same time.
            <div id="invoice-print-hide" className="mt-6 pt-6 border-t border-gray-100 space-y-2.5">
              <div className="relative">
                <Mail className="w-4 h-4 text-gray-300 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={emailOverride}
                  onChange={e => setEmailOverride(e.target.value)}
                  placeholder="Send to email"
                  type="email"
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:outline-none"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => onSend(emailOverride)}
                  disabled={sending || !emailOverride}
                  className="flex-1 text-white py-3 rounded-xl text-sm font-bold transition-opacity flex items-center justify-center gap-2 disabled:opacity-40 shadow-sm"
                  style={{ background: NAVY }}
                >
                  {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send Invoice</>}
                </button>
                <button
                  onClick={() => window.print()}
                  className="flex-1 bg-gray-50 border border-gray-200 text-gray-700 py-3 rounded-xl text-sm font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" /> Download PDF
                </button>
              </div>
            </div>
          ) : (
            // Sent / Paid: state a plain fact ("sent to X on Y"), then
            // give exactly two explicit actions — Download, and a
            // collapsed Resend — instead of leaving the driver to guess
            // whether anything is still clickable here.
            <div id="invoice-print-hide" className="mt-6 pt-6 border-t border-gray-100 space-y-3">
              <div className="rounded-xl p-3.5 flex items-start gap-2.5" style={{ background: statusMeta.bg }}>
                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: statusMeta.text }} />
                <p className="text-xs font-semibold" style={{ color: statusMeta.text }}>
                  {invoice.status === "paid"
                    ? "This invoice has been marked paid."
                    : `Sent to ${invoice.sent_to_email || "—"} on ${invoice.sent_at ? new Date(invoice.sent_at).toLocaleDateString() : "—"}.`}
                  {" "}This invoice is locked from further edits.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => window.print()}
                  className="flex-1 bg-gray-50 border border-gray-200 text-gray-700 py-3 rounded-xl text-sm font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" /> Download PDF
                </button>
                <button
                  onClick={() => setShowResend(v => !v)}
                  className="flex-1 border py-3 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                  style={{ borderColor: BORDER_BRASS, color: GOLD }}
                >
                  <Send className="w-4 h-4" /> {showResend ? "Cancel resend" : "Resend to another email"}
                </button>
              </div>
              {showResend && (
                <div className="space-y-2.5 pt-1">
                  <div className="relative">
                    <Mail className="w-4 h-4 text-gray-300 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      value={emailOverride}
                      onChange={e => setEmailOverride(e.target.value)}
                      placeholder="Send to email"
                      type="email"
                      className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:outline-none"
                    />
                  </div>
                  <button
                    onClick={() => onSend(emailOverride)}
                    disabled={sending || !emailOverride}
                    className="w-full text-white py-3 rounded-xl text-sm font-bold transition-opacity flex items-center justify-center gap-2 disabled:opacity-40 shadow-sm"
                    style={{ background: NAVY }}
                  >
                    {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Confirm Resend</>}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile sticky action bar — mirrors the desktop buttons above so
          the actions stay reachable one-handed on small screens, matching
          the mockup's mobile action bar. */}
      {invoice.status === "draft" && (
        <div id="invoice-print-hide" className="sm:hidden fixed bottom-0 left-0 w-full bg-white border-t border-gray-100 p-4 flex gap-3 shadow-[0_-6px_16px_-12px_rgba(0,0,0,0.2)] z-20">
          <button onClick={() => window.print()} className="flex-1 border border-gray-200 text-gray-700 text-sm font-bold py-2.5 rounded-lg">Download</button>
          <button onClick={() => onSend(emailOverride)} disabled={sending || !emailOverride} className="flex-1 text-white text-sm font-bold py-2.5 rounded-lg disabled:opacity-40" style={{ background: NAVY }}>Send</button>
        </div>
      )}
    </div>
  );
}
